import {useEffect, useRef, useState} from 'react'
import {Button, Col, Form, Modal, Nav, Row} from 'react-bootstrap'
import {ITicketFilters, ITicketSavedFilter} from '../../../types/tickets'
import {DateInput} from '../../../components/DateInput'

type OptionLike = {id: string; name?: string; firstName?: string; lastName?: string; email?: string}

interface ModalTicketFiltersProps {
  isOpen: boolean
  onClose: () => void
  filters: ITicketFilters
  setFilters: React.Dispatch<React.SetStateAction<ITicketFilters>>
  onApply: (filters: ITicketFilters) => void
  savedFilters?: ITicketSavedFilter[]
  onSaveFilter?: (name: string, filters: ITicketFilters) => Promise<ITicketSavedFilter | null>
  onUpdateFilter?: (id: string, name: string, filters: ITicketFilters) => Promise<ITicketSavedFilter | null>
  onDeleteFilter?: (id: string) => Promise<boolean>
  users: OptionLike[]
  groups: OptionLike[]
  ticketTypes: OptionLike[]
}

const buildUserLabel = (user: OptionLike) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || user.id

export const ModalTicketFilters: React.FC<ModalTicketFiltersProps> = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  onApply,
  savedFilters = [],
  onSaveFilter,
  onUpdateFilter,
  onDeleteFilter,
  users,
  groups,
  ticketTypes,
}) => {
  const [activeTab, setActiveTab] = useState<'filters' | 'saved'>('filters')
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string>('')
  const [savedFilterName, setSavedFilterName] = useState('')
  const [savedTabMode, setSavedTabMode] = useState<'manage' | 'save'>('manage')
  const savedNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (!selectedSavedFilterId) setSavedFilterName('')
  }, [isOpen, selectedSavedFilterId])

  useEffect(() => {
    if (!isOpen || activeTab !== 'saved' || savedTabMode !== 'save') return
    savedNameInputRef.current?.focus()
  }, [activeTab, isOpen, savedTabMode])

  const updateFilterValue = (key: keyof ITicketFilters, value: string | boolean) => {
    setFilters((prev) => ({...prev, [key]: value}))
  }

  const handleSelectSavedFilter = (id: string) => {
    setSelectedSavedFilterId(id)
    if (!id) {
      setSavedFilterName('')
      return
    }
    const selected = savedFilters.find((savedFilter) => savedFilter.id === id)
    if (!selected) return
    setSavedFilterName(selected.name)
    setFilters({...selected.filters})
  }

  const handleApply = () => {
    onApply(filters)
    onClose()
  }

  const handleSaveFilter = async () => {
    if (!onSaveFilter) return
    const saved = await onSaveFilter(savedFilterName, filters)
    if (!saved) return
    setSelectedSavedFilterId(saved.id)
    setSavedFilterName(saved.name)
  }

  const handleUpdateFilter = async () => {
    if (!onUpdateFilter || !selectedSavedFilterId) return
    const updated = await onUpdateFilter(selectedSavedFilterId, savedFilterName, filters)
    if (!updated) return
    setSelectedSavedFilterId(updated.id)
    setSavedFilterName(updated.name)
  }

  const handleDeleteFilter = async () => {
    if (!onDeleteFilter || !selectedSavedFilterId) return
    const confirmed = window.confirm('Delete this saved filter?')
    if (!confirmed) return
    const deleted = await onDeleteFilter(selectedSavedFilterId)
    if (!deleted) return
    setSelectedSavedFilterId('')
    setSavedFilterName('')
  }

  const goToSaveTab = () => {
    setActiveTab('saved')
    setSavedTabMode('save')
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='xl'>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Ticket Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex align-items-center justify-content-between mb-3 border-bottom pb-2'>
          <Nav
            variant='tabs'
            className='border-bottom-0'
            activeKey={activeTab}
            onSelect={(key) => {
              const nextTab = (key as 'filters' | 'saved') || 'filters'
              setActiveTab(nextTab)
              if (nextTab === 'saved') setSavedTabMode('manage')
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey='filters'>Filters</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='saved'>Saved filters</Nav.Link>
            </Nav.Item>
          </Nav>
          {activeTab === 'filters' && (
            <div className='d-flex gap-2'>
              <Button variant='dark' onClick={goToSaveTab}>
                <i className='bi bi-save me-1' />
                Save Filter
              </Button>
              <Button variant='primary' onClick={handleApply}>
                <i className='bi bi-search me-1' />
                Apply Filters
              </Button>
            </div>
          )}
        </div>

        {activeTab === 'saved' ? (
          <>
            <Row className='g-3'>
              <Col md={6}>
                <Form.Label>Saved filter</Form.Label>
                <Form.Select
                  value={selectedSavedFilterId}
                  onChange={(e) => handleSelectSavedFilter(e.target.value)}
                >
                  <option value=''>Select saved filter</option>
                  {savedFilters.map((savedFilter) => (
                    <option key={savedFilter.id} value={savedFilter.id}>
                      {savedFilter.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Filter name</Form.Label>
                <Form.Control
                  ref={savedNameInputRef}
                  value={savedFilterName}
                  placeholder='Enter filter name'
                  onChange={(e) => setSavedFilterName(e.target.value)}
                />
              </Col>
            </Row>
            <div className='d-flex gap-2 justify-content-end mt-3'>
              <Button variant='outline-secondary' onClick={() => setActiveTab('filters')}>
                Back to filters
              </Button>
              <Button variant='dark' onClick={handleSaveFilter}>
                Save as new
              </Button>
              <Button
                variant='primary'
                onClick={handleUpdateFilter}
                disabled={!selectedSavedFilterId}
              >
                Update
              </Button>
              <Button
                variant='danger'
                onClick={handleDeleteFilter}
                disabled={!selectedSavedFilterId}
              >
                Delete
              </Button>
              <Button
                variant='success'
                onClick={handleApply}
                disabled={!selectedSavedFilterId}
              >
                Apply selected
              </Button>
            </div>
          </>
        ) : (
          <Form>
            <Row className='g-3'>
              <Col md={4}>
                <Form.Label>Search text</Form.Label>
                <Form.Control
                  value={filters.search}
                  onChange={(e) => updateFilterValue('search', e.target.value)}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => updateFilterValue('status', e.target.value)}
                >
                  <option value=''>All status</option>
                  <option value='OPEN'>Open</option>
                  <option value='IN_PROGRESS'>In Progress</option>
                  <option value='WAITING_CUSTOMER'>Waiting Customer</option>
                  <option value='RESOLVED'>Resolved</option>
                  <option value='CLOSED'>Closed</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Channel</Form.Label>
                <Form.Select
                  value={filters.channel}
                  onChange={(e) => updateFilterValue('channel', e.target.value)}
                >
                  <option value=''>All channels</option>
                  <option value='INTERNAL'>Internal</option>
                  <option value='PHONE'>Phone</option>
                  <option value='WHATSAPP'>WhatsApp</option>
                  <option value='SMS'>SMS</option>
                  <option value='EMAIL'>Email</option>
                  <option value='WEB'>Web</option>
                  <option value='API'>API</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label>Priority</Form.Label>
                <Form.Select
                  value={filters.priority}
                  onChange={(e) => updateFilterValue('priority', e.target.value)}
                >
                  <option value=''>All priorities</option>
                  <option value='LOW'>Low</option>
                  <option value='MEDIUM'>Medium</option>
                  <option value='HIGH'>High</option>
                  <option value='URGENT'>Urgent</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Ticket type</Form.Label>
                <Form.Select
                  value={filters.ticketTypeId}
                  onChange={(e) => updateFilterValue('ticketTypeId', e.target.value)}
                >
                  <option value=''>All types</option>
                  {ticketTypes.map((ticketType) => (
                    <option key={ticketType.id} value={ticketType.id}>
                      {ticketType.name || ticketType.id}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Assignment type</Form.Label>
                <Form.Select
                  value={filters.assignmentType}
                  onChange={(e) => updateFilterValue('assignmentType', e.target.value)}
                >
                  <option value=''>All assignments</option>
                  <option value='USER'>User</option>
                  <option value='GROUP'>Group</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Inbox mode</Form.Label>
                <Form.Select
                  value={filters.inboxMode}
                  onChange={(e) => updateFilterValue('inboxMode', e.target.value)}
                >
                  <option value=''>All</option>
                  <option value='MY'>My queue</option>
                  <option value='GROUP'>Group queue</option>
                  <option value='UNASSIGNED'>Unassigned</option>
                  <option value='OVERDUE'>Overdue</option>
                </Form.Select>
              </Col>

              <Col md={4}>
                <Form.Label>Assigned user</Form.Label>
                <Form.Select
                  value={filters.assignedUserId}
                  onChange={(e) => updateFilterValue('assignedUserId', e.target.value)}
                >
                  <option value=''>All users</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {buildUserLabel(user)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Assigned group</Form.Label>
                <Form.Select
                  value={filters.assignedGroupId}
                  onChange={(e) => updateFilterValue('assignedGroupId', e.target.value)}
                >
                  <option value=''>All groups</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name || group.id}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Created by</Form.Label>
                <Form.Select
                  value={filters.createdById}
                  onChange={(e) => updateFilterValue('createdById', e.target.value)}
                >
                  <option value=''>All creators</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {buildUserLabel(user)}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label>Created date from</Form.Label>
                <Form.Control
                  type='date'
                  value={filters.createdAfter}
                  onChange={(e) => updateFilterValue('createdAfter', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Created date to</Form.Label>
                <Form.Control
                  type='date'
                  value={filters.createdBefore}
                  onChange={(e) => updateFilterValue('createdBefore', e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Due date from</Form.Label>
                <DateInput
                  id='ticket-due-after'
                  name='dueAfter'
                  value={filters.dueAfter}
                  onChange={(_, value) => updateFilterValue('dueAfter', value || '')}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Due date to</Form.Label>
                <DateInput
                  id='ticket-due-before'
                  name='dueBefore'
                  value={filters.dueBefore}
                  onChange={(_, value) => updateFilterValue('dueBefore', value || '')}
                />
              </Col>
            </Row>
            <hr />
            <Row className='g-2'>
              <Col md='auto'>
                <Form.Check
                  id='pending-inbox-filter'
                  label='Pending inbox'
                  checked={filters.pendingInbox}
                  onChange={(e) => updateFilterValue('pendingInbox', e.target.checked)}
                />
              </Col>
              <Col md='auto'>
                <Form.Check
                  id='only-overdue-filter'
                  label='Only overdue'
                  checked={filters.onlyOverdue}
                  onChange={(e) => updateFilterValue('onlyOverdue', e.target.checked)}
                />
              </Col>
              <Col md='auto'>
                <Form.Check
                  id='only-unassigned-filter'
                  label='Only unassigned'
                  checked={filters.onlyUnassigned}
                  onChange={(e) => updateFilterValue('onlyUnassigned', e.target.checked)}
                />
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onClose}>
          Cancel
        </Button>
        {activeTab === 'filters' && (
          <Button variant='primary' onClick={handleApply}>
            Apply Filters
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
